import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Grow,
  Alert,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { grey } from '@mui/material/colors'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Visibility from '@mui/icons-material/Visibility'
import { useSnackbar } from 'notistack'
import _ from 'lodash'
import { matchRoutes, useLocation, useParams } from 'react-router-dom'
import { useQueryClient } from 'react-query'

import NotFound from 'components/NotFound'
import UserService from 'api/services/User'
import { useAuthStore } from 'store'
import { PASSWORD_REGEX } from 'helpers/constants'
import KeycloakService from 'services/KeycloakService'
import usePageView from 'hooks/usePageView'

interface FieldsForProfile {
  firstName: string
  lastName: string
  password: string
  username: string
  email: string
  city: string
  mobileNumber: string
}
interface State {
  showPassword: boolean
}

export default function UsersEdit() {
  const { sendPageView } = usePageView({ runOnMount: false })
  const location = useLocation()

  const { userId } = useParams()

  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const [local, setLocal] = React.useState<State>({
    showPassword: false,
  })

  const [localError, setLocalError] = React.useState<boolean>(false)

  const [notFound, setNotFound] = React.useState<boolean>(false)

  const { enqueueSnackbar } = useSnackbar()

  const [user, setUser] = useAuthStore((state) => [state.user, state.setUser])

  const { control, handleSubmit, reset } = useForm<FieldsForProfile>({
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
      city: '',
      mobileNumber: '',
      password: '',
    },
  })

  const currentUser = UserService.hooks.useCurrentUser({
    enabled: !!KeycloakService.isLoggedIn(),
    onSuccess: (data) => {
      setUser(data)
    },
  })

  const handleClickShowPassword = () => {
    setLocal({
      ...local,
      showPassword: !local.showPassword,
    })
  }

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()
  }

  const routes = [{ path: '/users/:userId' }, { path: '/profile' }]

  const matchedRoutes = matchRoutes(routes, location)

  const matchEdit =
    !!matchedRoutes?.length &&
    !!matchedRoutes.find(
      (matchedRoute) => matchedRoute?.route?.path === '/users/:userId'
    )

  const matchProfile =
    !!matchedRoutes?.length &&
    !!matchedRoutes.find(
      (matchedRoute) => matchedRoute?.route?.path === '/profile'
    )
  const matchProfileFallback = matchEdit ? userId : 'unreachable'
  const USER_TO_FETCH = matchProfile ? user?.userId : matchProfileFallback

  const userByIdQuery = UserService.hooks.useUserById(Number(USER_TO_FETCH), {
    onSettled: () => {
      sendPageView()
    },
    onSuccess: (data) => {
      if (data) {
        reset({
          firstName: data?.person?.firstName ?? ' ',
          lastName: data?.person?.lastName ?? '',
          mobileNumber: data?.person?.cinfo?.mobileNumber ?? '',
          email: data?.person?.cinfo?.email ?? '',
          username: data?.username ?? '',
        })
      }
    },
    onError: (err) => {
      if (err?.response?.status === 404) {
        setNotFound(true)
      } else {
        setLocalError(true)
      }
    },
  })

  const updateUserMutation = UserService.hooks.useUpdateUser({
    onSuccess: () => {
      currentUser.refetch()
      userByIdQuery.refetch()
      enqueueSnackbar(t('dashboard.profile.successMessage'), {
        variant: 'success',
      })
      if (KeycloakService.isAdmin()) {
        queryClient.invalidateQueries(UserService.keys.QueryKeys.all)
        queryClient.invalidateQueries(UserService.keys.QueryKeys.count)
      }
    },
    onError: (error) => {
      enqueueSnackbar(t('error.unspecific'), {
        variant: 'error',
      })
    },
  })

  const onSubmit = (data) => {
    if (!data.password) {
      data = _.omit(data, 'password')
    }
    updateUserMutation.mutate({ id: Number(USER_TO_FETCH), payload: data })
  }

  const handleCloseError = () => {
    setLocalError(false)
  }

  return (
    <>
      {userByIdQuery.isLoading ? (
        <Typography align="center" sx={{ mt: 4 }}>
          <CircularProgress color="secondary" size={30} />
        </Typography>
      ) : null}

      <Grow in={userByIdQuery.isError && notFound} unmountOnExit>
        <div>
          <NotFound />
        </div>
      </Grow>
      <Grow in={localError} unmountOnExit>
        <Alert
          severity="error"
          onClose={handleCloseError}
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          {t('error.unspecific')}
        </Alert>
      </Grow>
      <Grow in={userByIdQuery.isSuccess && !!userByIdQuery.data} unmountOnExit>
        <Box sx={{ justifyContent: 'center', display: 'flex', pb: 5 }}>
          <Box
            sx={{
              maxWidth: (theme) => theme.breakpoints.values.lg,
              border: 1,
              borderColor: grey[300],
              boxShadow: 25,
              borderRadius: 2.5,
              mt: 5,
              p: 6,
              bgcolor: 'background.paper',
            }}
          >
            <Grid
              container
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              spacing={2}
            >
              <Grid item xs={12}>
                <Typography variant="h6" textAlign="center">
                  {t('dashboard.profile.title')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  name="username"
                  control={control}
                  render={({
                    field: { onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <TextField
                      label={t('dashboard.profile.form.username')}
                      variant="filled"
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                      fullWidth
                      disabled
                      sx={{
                        color: 'common.black',
                        borderColor: grey[300],
                      }}
                      {...rest}
                    ></TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  rules={{
                    pattern: {
                      value: PASSWORD_REGEX,
                      message: t('error.password'),
                    },
                  }}
                  name="password"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label={t('userSignUpPage.password')}
                      variant="filled"
                      error={!!error}
                      type={local.showPassword ? 'text' : 'password'}
                      fullWidth
                      helperText={error ? error?.message : ' '}
                      inputProps={{
                        autoComplete: 'new-password',
                        form: {
                          autoComplete: 'off',
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              color={local.showPassword ? 'primary' : 'default'}
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {local.showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        color: 'common.black',
                        borderColor: grey[300],
                      }}
                      {...field}
                    ></TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  name="firstName"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label={t('dashboard.profile.form.firstName')}
                      variant="filled"
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                      fullWidth
                      sx={{
                        color: 'common.black',
                        borderColor: grey[300],
                      }}
                      {...field}
                    ></TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  name="lastName"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label={t('dashboard.profile.form.lastName')}
                      variant="filled"
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                      fullWidth
                      sx={{
                        color: 'common.black',
                        borderColor: grey[300],
                      }}
                      {...field}
                    ></TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="mobileNumber"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label={t('dashboard.profile.form.phoneNumber')}
                      variant="filled"
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                      fullWidth
                      sx={{
                        color: 'common.black',
                        borderColor: grey[300],
                      }}
                      {...field}
                    ></TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label={t('dashboard.profile.form.email')}
                      variant="filled"
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                      fullWidth
                      sx={{
                        color: 'common.black',
                        borderColor: grey[300],
                      }}
                      {...field}
                    ></TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ width: '100%', textTransform: 'none' }}
                >
                  {updateUserMutation.isLoading ? (
                    <CircularProgress
                      sx={{
                        '& svg': {
                          color: 'background.paper',
                        },
                      }}
                      size={30}
                    />
                  ) : (
                    <Typography fontSize={20}>
                      {t('dashboard.profile.form.button')}
                    </Typography>
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Grow>
    </>
  )
}
